import React, { useState } from 'react'
import { Modal } from "@mui/material";
import { Close } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { path } from '../../path';
import FunnelChart from './FunnelChart';
import CommonDateSelectionForm from './CommonDateSelectionForm';

const AppAnalyticsTable = ({ data }) => {
  if (!data || data.length === 0) { return null; }

  const renderBreakdown = (obj) => {
    return Object.entries(obj || {}).map(([key, val]) => (
      <div key={key}>
        <strong>{key}</strong> :
        <span className="ml-1">Total {val.total}</span>
        <span className="ml-2 text-gray-600">Unique {val.uniqueUsers}</span>
      </div>
    ));
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-3">📱 App Analytics</h3>

      <table className="w-full border text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 border">Event</th>
            <th className="p-2 border">Total</th>
            <th className="p-2 border">Unique</th>
            <th className="p-2 border">Platform</th>
            <th className="p-2 border">App Version</th>
          </tr>
        </thead>

        <tbody>
          {data.map(event => (
            <tr key={event.type}>
              <td className="p-2 border">{event.type}</td>
              <td className="p-2 border">{event.total}</td>
              <td className="p-2 border">{event.uniqueUsers}</td>

              <td className="p-2 border">
                {renderBreakdown(event.platforms)}
              </td>

              <td className="p-2 border">
                {renderBreakdown(event.versions)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- EVENT TABLE ----------
const EventTable = ({ title, data }) => {
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-2">{title}</h3>

      <table className="w-full border text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 border">Event Type</th>
            <th className="p-2 border">Total Events</th>
            <th className="p-2 border">Unique Users</th>
          </tr>
        </thead>

        <tbody>
          {data.map((item) => (
            <tr key={item.type}>
              <td className="p-2 border capitalize">{item.type}</td>
              <td className="p-2 border">{item.total}</td>
              <td className="p-2 border">{item.uniqueUsers}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AuthProviderTable = ({ data }) => {
  if (!data || data.length === 0) { return null; }

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-2">🔐 Signup Auth Providers</h3>

      <table className="w-full border text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 border">Provider</th>
            <th className="p-2 border">Users</th>
          </tr>
        </thead>

        <tbody>
          {data.map(item => (
            <tr key={item.provider}>
              <td className="p-2 border">{item.provider}</td>
              <td className="p-2 border">{item.totalUsers}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UTMCampaignTable = ({ data }) => {
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-2">📊 Install Attribution</h3>

      <table className="w-full border text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 border">Source</th>
            <th className="p-2 border">Medium</th>
            <th className="p-2 border">Campaign</th>
            <th className="p-2 border">Ad Set</th>
            <th className="p-2 border">Ad Name</th>
            <th className="p-2 border">Unique Users</th>
          </tr>
        </thead>

        <tbody>
          {data.map((item) => (
            <tr key={`${item.source}-${item.medium}-${item.campaign}-${item.adSet}-${item.adName}`}>
              <td className="p-2 border">{item.source}</td>
              <td className="p-2 border">{item.medium}</td>
              <td className="p-2 border">{item.campaign}</td>
              <td className="p-2 border">{item.adSet}</td>
              <td className="p-2 border">{item.adName}</td>
              <td className="p-2 border">{item.uniqueUsers}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AnalyticsModel = ({ open, toggle }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);


  const onSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);

      const fromDate = event.target['fromDate']?.value;
      const toDate = event.target['toDate']?.value;

      if (!fromDate || !toDate) {
        toast.error("Date cannot be empty!");
        return;
      }

      if (new Date(fromDate) > new Date(toDate)) {
        toast.error("From date cannot be greater than to date!");
        return;
      }

      if (new Date(toDate) > Date.now()) {
        toast.error("To date cannot be in the future!");
        return;
      }

      const res = await axios.get(
        `${path}/analytics?fromDate=${fromDate}&toDate=${toDate}`
      );

      setData(res.data);

    } catch (error) {
      toast.error("Error loading analytics");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal
      open={open}
      onClose={toggle}
      className="flex items-center justify-center"
    >

      <div className="bg-white p-5 rounded w-[650px] max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-bold">Analytics</h2>
          <Close className="cursor-pointer" onClick={toggle} />
        </div>


        <CommonDateSelectionForm btnText="Fetch Analytics" onSubmitHandler={onSubmitHandler} loading={loading} />

        {/* RESULTS */}
        {data && !loading && (
          <div className="mt-6">
            <FunnelChart
              appAnalytics={data.appAnalytics}
              utmAnalytics={data.utmAnalytics}
              webAdEvents={data.webAdEvents}
              authProviderAnalytics={data.authProviderAnalytics}
            />
            <AppAnalyticsTable data={data.appAnalytics} />
            <UTMCampaignTable data={data.utmAnalytics} />
            <EventTable title="🌐 Web Events" data={data.webEvents} />
            <EventTable title="📢 Web Ad Events" data={data.webAdEvents} />
            <AuthProviderTable data={data.authProviderAnalytics} />
          </div>
        )}


      </div>
    </Modal>
  );
};

export default AnalyticsModel;